import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import {
  $isListNode,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $createHeadingNode, $createQuoteNode, $isHeadingNode } from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from '@lexical/utils'
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_NORMAL,
  ElementNode,
  FORMAT_TEXT_COMMAND,
  KEY_MODIFIER_COMMAND,
  LexicalNode,
  SELECTION_CHANGE_COMMAND,
  TextNode,
} from 'lexical'
import { Dispatch, useCallback, useEffect, useState, FC } from 'react'
import * as React from 'react'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import AddLinkIcon from '@mui/icons-material/AddLink'
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS'
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto'
import TableChartIcon from '@mui/icons-material/TableChart'
import { Button, Divider, Select, MenuItem, Box, ToggleButton, ToggleButtonGroup, Theme, SxProps } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { $getTableCellNodeFromLexicalNode } from '@lexical/table'

import { getSelectedNode } from '../../utils/getSelectedNode'
import { sanitizeUrl } from '../../utils/url'
import { ImageDialog } from '../../ui/ImageDialog'
import { INSERT_IMAGE_COMMAND } from '../ImagesPlugin'
import { InsertTableDialog } from '../TablePlugin'
import { useCustomTranslation } from '../../../../Hooks/useCustomTranslation'
import { ImagePayload } from '../../nodes/ImageNode'
import { NotificationImageUploadResponse } from '@e3dc-react-apps/portal/src/Libs/notifications/models/notifications.model'

const useStyles = makeStyles()(theme => ({
  toolbar: {
    display: 'flex',
    marginBottom: '1px',
    background: theme.shape.markdownEditor.background,
    padding: `${theme.spacing(1)} ${theme.spacing(3)}`,
    borderTopLeftRadius: '10px',
    borderTopRightRadius: '10px',
    alignItems: 'center',
    position: 'sticky',
    top: '0',
    zIndex: '2',
    '@media (max-width: 850px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  butonsMainContainer: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    '@media (max-width: 850px)': {
      marginTop: theme.spacing(1),
      marginLeft: `-${theme.spacing(1)}}`,
    },
  },
  buttonsContainer: {
    display: 'flex',
    alignItems: 'center',
    height: '40px',
  },
  button: {
    padding: theme.spacing(1),
    minWidth: 40,
    minHeight: 40,
    marginLeft: theme.spacing(1),
    svg: {
      height: 'auto',
    },
    borderRadius: theme.shape.borderRadius,
  },
  buttonIcon: {
    width: 24,
  },
  divider: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(2),
    '@media (max-width: 850px)': {
      ':first-of-type': {
        display: 'none',
      },
    },
  },
  select: {
    fontSize: theme.shape.select.fontSize,
  },
}))

type BlockTypes = 'paragraph' | 'h1' | 'h2' | 'h3' | 'bullet' | 'check' | 'number' | 'quote'

interface BlockTypeOption {
  value: BlockTypes
  label: string
}

const blockTypeOptions: BlockTypeOption[] = [
  {
    value: 'paragraph',
    label: 'shell.markdown-editor.normal',
  },
  {
    value: 'h1',
    label: 'shell.markdown-editor.heading-1',
  },
  {
    value: 'h2',
    label: 'shell.markdown-editor.heading-2',
  },
  {
    value: 'h3',
    label: 'shell.markdown-editor.heading-3',
  },
  {
    value: 'bullet',
    label: 'shell.markdown-editor.bulleted-list',
  },
  {
    value: 'check',
    label: 'shell.markdown-editor.check-list',
  },
  {
    value: 'number',
    label: 'shell.markdown-editor.numbered-list',
  },
  {
    value: 'quote',
    label: 'shell.markdown-editor.quote',
  },
]

const tableBlockTypeOptions: BlockTypeOption[] = [
  {
    value: 'paragraph',
    label: 'shell.markdown-editor.normal',
  },
  {
    value: 'bullet',
    label: 'shell.markdown-editor.bulleted-list',
  },
  {
    value: 'number',
    label: 'shell.markdown-editor.numbered-list',
  },
]

interface ToolbarPluginProps {
  setIsLinkEditMode: Dispatch<boolean>
  sx?: SxProps<Theme>
  onImageDropped?: (image: File | undefined) => Promise<NotificationImageUploadResponse | undefined>
  isLoading?: boolean
}

/**
 * This component gives opportunity for use formatting functionality
 */
export const ToolbarPlugin: FC<ToolbarPluginProps> = ({ setIsLinkEditMode, sx, onImageDropped, isLoading }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const [editor] = useLexicalComposerContext()
  const [activeEditor, setActiveEditor] = useState(editor)
  const [blockType, setBlockType] = useState<BlockTypes>('paragraph')
  const [isLink, setIsLink] = useState(false)
  const [isBold, setIsBold] = useState(false)
  const [isStrikethrough, setIsStrikethrough] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isOpenedInsertImage, setIsOpenedInsertImage] = useState<boolean>(false)
  const [isOpenedInsertTable, setIsOpenedInsertTable] = useState<boolean>(false)
  const [isEditable, setIsEditable] = useState(() => editor.isEditable())
  const [isTableCellFocused, setIsTableCellFocused] = useState(false)

  useEffect(() => {
    return editor.registerUpdateListener(() => {
      editor.getEditorState().read(() => {
        const selection = $getSelection()

        if (!$isRangeSelection(selection)) {
          setIsTableCellFocused(false)
          return
        }

        const tableCellNodeFromSelection = $getTableCellNodeFromLexicalNode(selection.anchor.getNode())

        if (!tableCellNodeFromSelection) {
          setIsTableCellFocused(false)
          return
        }

        setIsTableCellFocused(true)
      })
    })
  }, [editor, setIsTableCellFocused])

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection()
    const getElement = (anchorNode: TextNode | ElementNode): TextNode | LexicalNode | ElementNode => {
      const element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, e => {
              const parent = e.getParent()
              return parent !== null && $isRootOrShadowRoot(parent)
            })

      return element ?? anchorNode.getTopLevelElementOrThrow()
    }

    const handleBlockTypeSetting = (element: TextNode | LexicalNode | ElementNode, anchorNode: ElementNode | TextNode): void => {
      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode)
        const type = parentList ? parentList.getListType() : element.getListType()
        setBlockType(type)
      } else {
        const type = $isHeadingNode(element) ? element.getTag() : element.getType()
        const isIncludedInList = blockTypeOptions.some(elem => elem.value === type)

        if (isIncludedInList) {
          setBlockType(type as BlockTypes)
        }
      }
    }

    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode()
      const element = getElement(anchorNode)
      const elementKey = element.getKey()
      const elementDOM = activeEditor.getElementByKey(elementKey)

      // Update text format
      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsStrikethrough(selection.hasFormat('strikethrough'))

      // Update links
      const node = getSelectedNode(selection)
      const parent = node.getParent()
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true)
      } else {
        setIsLink(false)
      }

      if (elementDOM !== null) {
        handleBlockTypeSetting(element, anchorNode)
      }
    }
  }, [activeEditor])

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        $updateToolbar()
        setActiveEditor(newEditor)
        return false
      },
      COMMAND_PRIORITY_CRITICAL
    )
  }, [editor, $updateToolbar])

  useEffect(() => {
    return mergeRegister(
      editor.registerEditableListener(editable => {
        setIsEditable(editable)
      }),
      activeEditor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar()
        })
      })
    )
  }, [$updateToolbar, activeEditor, editor])

  useEffect(() => {
    return activeEditor.registerCommand(
      KEY_MODIFIER_COMMAND,
      payload => {
        const event: KeyboardEvent = payload
        const { code, ctrlKey, metaKey } = event

        if (code === 'KeyK' && (ctrlKey || metaKey)) {
          event.preventDefault()
          let url: string | null
          if (!isLink) {
            setIsLinkEditMode(true)
            url = sanitizeUrl('https://')
          } else {
            setIsLinkEditMode(false)
            url = null
          }
          return activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, url)
        }
        return false
      },
      COMMAND_PRIORITY_NORMAL
    )
  }, [activeEditor, isLink, setIsLinkEditMode])

  const insertLink = useCallback(() => {
    if (!isLink) {
      setIsLinkEditMode(true)
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl('https://'))
    } else {
      setIsLinkEditMode(false)
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
    }
  }, [editor, isLink, setIsLinkEditMode])

  const onSelectBlockType = (value: BlockTypes): void => {
    switch (value) {
      case 'paragraph': {
        editor.update(() => {
          const selection = $getSelection()
          $setBlocksType(selection, () => $createParagraphNode())
        })
        break
      }
      case 'h1':
      case 'h2':
      case 'h3': {
        if (blockType !== value) {
          editor.update(() => {
            const selection = $getSelection()
            $setBlocksType(selection, () => $createHeadingNode(value))
          })
        }
        break
      }
      case 'bullet': {
        if (blockType !== 'bullet') {
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
        } else {
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
        }
        break
      }
      case 'check': {
        if (blockType !== 'check') {
          editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined)
        } else {
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
        }
        break
      }
      case 'number': {
        if (blockType !== 'number') {
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
        } else {
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
        }
        break
      }
      case 'quote': {
        if (blockType !== 'quote') {
          editor.update(() => {
            const selection = $getSelection()
            $setBlocksType(selection, () => $createQuoteNode())
          })
        }
        break
      }
    }
  }

  const openInsertImage = (): void => {
    setIsOpenedInsertImage(true)
  }

  const insertImage = (formData: ImagePayload): void => {
    activeEditor.dispatchCommand(INSERT_IMAGE_COMMAND, formData)
  }

  const closeInsertImage = (): void => {
    setIsOpenedInsertImage(false)
  }

  const openInsertTable = (): void => {
    setIsOpenedInsertTable(true)
  }

  const closeInsertTable = (): void => {
    setIsOpenedInsertTable(false)
  }

  return (
    <Box className={classes.toolbar} sx={sx}>
      {isOpenedInsertImage && (
        <ImageDialog
          open={isOpenedInsertImage}
          onClose={closeInsertImage}
          onSave={insertImage}
          onImageDropped={onImageDropped}
          isLoading={isLoading}
        />
      )}
      {isOpenedInsertTable && <InsertTableDialog activeEditor={activeEditor} open={isOpenedInsertTable} onClose={closeInsertTable} />}
      {activeEditor === editor && (
        <Select
          value={blockType}
          size="small"
          onChange={e => {
            onSelectBlockType(e.target.value as BlockTypes)
          }}
          classes={{ select: classes.select }}
        >
          {(isTableCellFocused ? tableBlockTypeOptions : blockTypeOptions).map(elem => (
            <MenuItem value={elem.value} key={elem.value}>
              {t(elem.label)}
            </MenuItem>
          ))}
        </Select>
      )}
      <Box className={classes.butonsMainContainer}>
        <Box className={classes.buttonsContainer}>
          <Divider orientation="vertical" className={classes.divider} />
          <ToggleButtonGroup value={isBold}>
            <ToggleButton
              disabled={!isEditable}
              onClick={() => {
                activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')
              }}
              aria-label={t('shell.markdown-editor.format-text-as-bold')}
              title={t('shell.markdown-editor.bold')}
              className={classes.button}
              value
            >
              <FormatBoldIcon className={classes.buttonIcon} />
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup value={isItalic}>
            <ToggleButton
              disabled={!isEditable}
              onClick={() => {
                activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
              }}
              aria-label={t('shell.markdown-editor.format-text-as-italics')}
              title={t('shell.markdown-editor.italic')}
              className={classes.button}
              value
            >
              <FormatItalicIcon className={classes.buttonIcon} />
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup value={isStrikethrough}>
            <ToggleButton
              disabled={!isEditable}
              onClick={() => {
                activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')
              }}
              aria-label={t('shell.markdown-editor.format-with-strikethrough')}
              title={t('shell.markdown-editor.strikethrough')}
              className={classes.button}
              value
            >
              <StrikethroughSIcon className={classes.buttonIcon} />
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup value={isLink}>
            <ToggleButton
              disabled={!isEditable}
              onClick={insertLink}
              aria-label={t('shell.markdown-editor.insert-link')}
              title={t('shell.markdown-editor.insert-link')}
              className={classes.button}
              value
            >
              <AddLinkIcon className={classes.buttonIcon} />
            </ToggleButton>
          </ToggleButtonGroup>
          <Divider orientation="vertical" className={classes.divider} />
        </Box>
        <Box className={classes.buttonsContainer}>
          <Button
            disabled={!isEditable}
            onClick={openInsertImage}
            aria-label={t('shell.markdown-editor.insert-image')}
            title={t('shell.markdown-editor.image')}
            className={classes.button}
          >
            <InsertPhotoIcon className={classes.buttonIcon} />
          </Button>
          <Button
            disabled={!isEditable || isTableCellFocused}
            onClick={openInsertTable}
            aria-label={t('shell.markdown-editor.insert-table')}
            title={t('shell.markdown-editor.table')}
            className={classes.button}
          >
            <TableChartIcon className={classes.buttonIcon} />
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
